import * as React from 'react';

import { Box, Button, Link, Stack } from '@mui/material';
import { CircleCheckIcon, MailIcon } from 'lucide-react';

import { useEmailClientConnect } from '@shared/email/emailClientConnect';
import { useLocale } from '@shared/hooks/locale';

type Props = {
  onSuccess: () => void;
  onError: () => void;
};

const Benefit = ({ children }: { children: React.ReactNode }) => (
  <Stack
    direction="row"
    spacing={1}
    sx={{
      alignItems: 'center',
    }}
  >
    <CircleCheckIcon size={18} color={'var(--mui-palette-primary-main)'} />
    <Box>{children}</Box>
  </Stack>
);

export const ConnectEmailPanel = ({ onSuccess, onError }: Props) => {
  const { t } = useLocale();
  const connectEmailClient = useEmailClientConnect();
  const [connectionError, setConnectionError] = React.useState<string | null>(
    null,
  );

  return (
    <Box>
      <Box>{t('increaseYourProductiveByLinkingYourEmails')}</Box>

      <Stack spacing={1} sx={{ mt: 3 }}>
        <Benefit>{t('sendEmailFromTheCRM')}</Benefit>
        <Benefit>{t('receiveAndShareEmailsWithinYourOrganisation')}</Benefit>
        <Benefit>{t('respondToEnquiriesAutomaticallyWithTemplates')}</Benefit>
        <Benefit>{t('trackEmailOpensAndClicks')}</Benefit>
      </Stack>

      {connectionError != null && <Box sx={{ mt: 3 }}>{connectionError}</Box>}

      <Box sx={{ mt: 3 }}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<MailIcon />}
          onClick={() => {
            connectEmailClient(error => {
              if (error == null) {
                onSuccess();
              } else {
                if (error.message !== 'access_denied') {
                  console.error(error);
                  setConnectionError(error.message);
                }
                onError();
              }
            });
          }}
        >
          {t('connectEmail')}
        </Button>
      </Box>

      <Box sx={{ mt: 3, mb: 1 }}>
        {t('theEmailSyncServicePoweredByNylas')} {t('subjectToTheir')}{' '}
        <Link
          color="primary"
          target="_blank"
          href="https://www.nylas.com/legal/terms/"
        >
          {t('termsOfUse')}
        </Link>{' '}
        {t('and')}{' '}
        <Link
          color="primary"
          target="_blank"
          href="https://www.nylas.com/privacy-policy/"
        >
          {t('privacyPolicy')}.
        </Link>
      </Box>
      <Box
        sx={{ mt: 3, mb: 1 }}
        dangerouslySetInnerHTML={{
          __html: t('loginGoogleDisclaimer', {
            dataPolicyLink: `https://developers.google.com/terms/api-services-user-data-policy`,
          }),
        }}
      />
    </Box>
  );
};
